import { useEffect, useState } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { api } from '@/lib/api'
import { ErrorState, Skeleton } from '@/components/States'

// A23.08 — Jamoalar (communities). Backend: GET /admin/communities (`handlers/communities.go`
// AdminList → communities + community_groups). Foydalanuvchi-yaratgan kanal/guruh to'plamlari.
interface Community {
  id: number
  name: string
  owner: string
  owner_id: number
  group_count: number
  created_at: string
}

export function CommunitiesScreen() {
  const t = useT()
  const [items, setItems] = useState<Community[]>([])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    api
      .get<{ communities: Community[] }>('/admin/communities', { signal: ac.signal })
      .then((d) => {
        if (alive) setItems(d.communities ?? [])
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const ql = q.trim().toLowerCase()
  const rows = ql ? items.filter((c) => c.name.toLowerCase().includes(ql) || c.owner.toLowerCase().includes(ql)) : items

  return (
    <>
      <PageHeader
        title={t.community.title}
        screenId="A23.08"
        description={t.community.note}
        actions={
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.common.search}
            className="w-44 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-primary placeholder:text-tertiary"
          />
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : loading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !rows.length ? (
          <div className="py-10 text-center text-sm text-tertiary">{t.dash.noData}</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border-default">
            <table className="w-full min-w-[560px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border-default bg-surface-2 text-left text-xs text-tertiary">
                  <th className="px-3 py-2 font-medium">{t.community.colName}</th>
                  <th className="px-3 py-2 font-medium">{t.community.colOwner}</th>
                  <th className="px-3 py-2 text-right font-medium">{t.community.colGroups}</th>
                  <th className="px-3 py-2 font-medium">{t.community.colCreated}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className="border-b border-border-subtle hover:bg-surface-2/50">
                    <td className="px-3 py-2 text-primary">{c.name}</td>
                    <td className="px-3 py-2 text-secondary">{c.owner}</td>
                    <td className="px-3 py-2 text-right font-mono tabular text-primary">{c.group_count.toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono text-xs text-tertiary">{c.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
